import { useEffect, useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
// Hooks
import { useSelector } from "react-redux";
import { usePaginatedResource } from "@/Common/hooks/Pagination";
import { useSubscriptions } from "@Common/context/subscriptions/Subscriptions";
import { buildGetNextPageParam } from "@/Common/hooks/Pagination/helpers";
// Queries
import { getTechnicianFlaggedTime, getTechnicianIsFinalized } from "@/DataPages/api/technicianFlaggedTime/queries";
// API
import { onUpdateTechnicianPerformance, onUpdateTechnicianFlaggedTime } from "@/DataPages/api/technicianFlaggedTime/subscriptions";

// Types
import { RootState } from "@/redux/store";
import { IPageParam, IPaginatedResponse } from "@/Common/api/types/pagination";
import { ITechnicianFlaggedTimeBase } from "@/DataPages/types/technicianFlaggedTime/model";
import { GraphqlSubscriptionResponse } from "@/Common/api/types/GraphqlSubscriptionResponse";

const PAGINATION_SIZE = 500;

interface IUseNotificationListConfiguration {
	enabled?: boolean;
	showOnlyUnread?: boolean;
	cycleId?: string;
	payPeriodId?: string;
}

export const useTechnicianFlaggedTimeQuery = ({ enabled = true, cycleId, payPeriodId }: IUseNotificationListConfiguration) => {
	const locationId = useSelector((state: RootState) => state.location.location?.locationId);
	const [technicianPerformanceBase, setTechnicianPerformanceBase] = useState<ITechnicianFlaggedTimeBase | null>(null);
	const { addSubscription, getSubscription } = useSubscriptions();
	const queryClient = useQueryClient();

	const {data: technicianPerformanceData} = useQuery({
		queryKey: ["getTechnicianIsFinalized", locationId, cycleId, payPeriodId],
		queryFn: async () => {
			if (!locationId || !cycleId || !payPeriodId) {
				throw new Error("Cant request TechnicianIsFinalized without all the required parameters");
			}

			const result = await getTechnicianIsFinalized(
				locationId,
				cycleId,
				payPeriodId,
			)

			if (!result) {
				throw new Error("Error retrieving TechnicianIsFinalized data");
			}

			return result;
		},
		enabled: enabled && Boolean(locationId) && Boolean(cycleId) && Boolean(payPeriodId),
		staleTime: Infinity,
	});

	const { data: repairOrders, ...otherResults } = usePaginatedResource({
		queryKey: ["technicianPerformance", locationId, cycleId, payPeriodId],
		queryFn: async ({ pageParam }) => {
			if (!locationId || !cycleId || !payPeriodId) {
				throw new Error("Cant request TechnicianFlaggedTime without all the required parameters");
			}

			const typedPageParam = pageParam as IPageParam<string>;
			if (!typedPageParam || !("after" in typedPageParam) || !("first" in typedPageParam)) {
				throw new Error("PageParam should have properties 'after' and 'first'");
			}

			const paginatedResponse = await getTechnicianFlaggedTime(
				locationId,
				cycleId,
				payPeriodId,
				typedPageParam.first,
				typedPageParam.after
			);

			if (!paginatedResponse) {
				throw new Error("Error retrieving technician flagged time");
			}

			return paginatedResponse as unknown as IPaginatedResponse<ITechnicianFlaggedTimeBase>;
		},
		getNextPageParam: buildGetNextPageParam(PAGINATION_SIZE),
		enabled: enabled && Boolean(locationId) && Boolean(cycleId) && Boolean(payPeriodId),
		initialPageParam: { after: "", first: PAGINATION_SIZE },
		staleTime: Infinity,
		gcTime: 5000,
		fetchAllPages: true,
	});

	useEffect(() => {
		if (!technicianPerformanceData) {
			return; 
		}

		setTechnicianPerformanceBase(technicianPerformanceData);

		return () => {};
	}, [technicianPerformanceData]);

	// Subscriptions
	const technicianPerformanceSubscription = getSubscription(["updateTechnicianPerformance", technicianPerformanceBase?._id]);
	technicianPerformanceSubscription?.subscriptions?.onUpdateTechnicianPerformance?.setCallback(
		(result: GraphqlSubscriptionResponse<"onUpdateTechnicianPerformance", ITechnicianFlaggedTimeBase>) => {
			const data = result.data?.onUpdateTechnicianPerformance;

			if (!data) {
				throw new Error("Error retrieving updateTechnicianPerformance data from subscription");
			}

			queryClient.invalidateQueries({
				queryKey: ["getTechnicianIsFinalized", data.locationId, data.commissionCycleId, data.payPeriodId],
			});
		}
	);

	const technicianPerformance = useMemo(() => {
		if (!repairOrders || !technicianPerformanceBase) {
			return null;
		}

		return { ...technicianPerformanceBase, repairOrders};
	}, [technicianPerformanceBase, repairOrders]);

	useEffect(() => {
		if (!technicianPerformanceBase) { 
			return;
		}

		addSubscription(
			["updateTechnicianPerformance", technicianPerformanceBase._id],
			[
				{
					context: "onUpdateTechnicianPerformance",
					query: onUpdateTechnicianPerformance,
					variables: {
						_id: technicianPerformanceBase._id,
					},
				},
			]
		);

		return () => {};
	}, [technicianPerformanceBase]);

	const subUpdateTechnicianFlaggedTime = getSubscription(["updateTechnicianFlaggedTime", locationId, cycleId, payPeriodId]);

	subUpdateTechnicianFlaggedTime?.subscriptions?.onUpdateTechnicianFlaggedTime?.setCallback(
		(
			result: GraphqlSubscriptionResponse<
				"onUpdateTechnicianFlaggedTime",
				{
					locationId: string;
					commissionCycleId: string;
					payPeriodId: string;
				}
			>
		) => {
			const payload = result.data?.onUpdateTechnicianFlaggedTime;
			if (!payload) return;

			// fallback just in case
			queryClient.invalidateQueries({
				queryKey: ["technicianPerformance", payload.locationId, payload.commissionCycleId, payload.payPeriodId],
			});
		}
	);

	useEffect(() => {
		if (!locationId || !cycleId || !payPeriodId) return;

		addSubscription(
			["updateTechnicianFlaggedTime", locationId, cycleId, payPeriodId],
			[
				{
					context: "onUpdateTechnicianFlaggedTime",
					query: onUpdateTechnicianFlaggedTime,
					variables: { locationId, commissionCycleId: cycleId, payPeriodId },
				},
			]
		);

		return () => {};
	}, [locationId, cycleId, payPeriodId]);

	return {
		technicianPerformance,
		...otherResults,
	};
};
